import axiosInstance from './axios';
import {
  CreatePaymentSettingDto,
  UpdatePaymentSettingDto,
  CreateDepositMethodDto,
  CreateWithdrawMethodDto,
} from 'types/paymentSetting';

const BASE_URL = '/admin/payments/settings';

export const paymentSettingsService = {
  getSettings: (siteId: string | number, params?: any) =>
    axiosInstance.get(BASE_URL, { params: { siteId, ...params } }),
  createSetting: (data: CreatePaymentSettingDto) => axiosInstance.post(BASE_URL, data),
  updateSetting: (id: number, data: UpdatePaymentSettingDto) =>
    axiosInstance.patch(`${BASE_URL}/${id}`, data),
  deleteSetting: (id: number) => axiosInstance.delete(`${BASE_URL}/${id}`),
  getWalletBalance: (id: number) => axiosInstance.get(`${BASE_URL}/${id}/wallet-balance`),
  getDepositMethods: (siteId: string | number) =>
    axiosInstance.get('/admin/payments/deposit-methods', { params: { siteId } }),
  createDepositMethod: (data: CreateDepositMethodDto) =>
    axiosInstance.post('/admin/payments/deposit-methods', data),
  uploadDepositMethodImage: (id: number, image: File) => {
    const formData = new FormData();
    formData.append('image', image);
    return axiosInstance.post(`/admin/payments/deposit-methods/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDepositMethodImage: (id: number) =>
    axiosInstance.delete(`/admin/payments/deposit-methods/${id}/image`),
  toggleDepositMethod: (id: number, isEnabled: boolean) =>
    axiosInstance.patch(`/admin/payments/deposit-methods/${id}/toggle`, { isEnabled }),
  updateDepositMethod: (id: number, data: any) =>
    axiosInstance.patch(`/admin/payments/deposit-methods/${id}`, data),
  updateDepositMethodsOrder: (items: { id: number; sortOrder: number }[]) =>
    axiosInstance.patch('/admin/payments/deposit-methods/reorder', { items }),

  // Withdraw Methods
  getWithdrawMethods: (siteId: string | number) =>
    axiosInstance.get(`/admin/payments/withdraw-methods?siteId=${siteId}`),
  uploadWithdrawMethodImage: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return axiosInstance.post(`/admin/payments/withdraw-methods/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteWithdrawMethodImage: (id: number) =>
    axiosInstance.delete(`/admin/payments/withdraw-methods/${id}/image`),
  toggleWithdrawMethod: (id: number, isEnabled: boolean) =>
    axiosInstance.patch(`/admin/payments/withdraw-methods/${id}/toggle`, { isEnabled }),
  updateWithdrawMethod: (id: number, data: { name?: string; minAmount?: string | number | null }) =>
    axiosInstance.patch(`/admin/payments/withdraw-methods/${id}`, data),
  updateWithdrawMethodsOrder: (items: { id: number; sortOrder: number }[]) =>
    axiosInstance.patch('/admin/payments/withdraw-methods/reorder', { items }),
  createWithdrawMethod: (data: CreateWithdrawMethodDto) =>
    axiosInstance.post('/admin/payments/withdraw-methods', data),

  // ── Catalogue: coins (sitePaymentAssets) and networks (sitePaymentNetworks) ──
  toggleAsset: (id: number, isEnabled: boolean) =>
    axiosInstance.patch(`/admin/payments/assets/${id}/toggle`, { isEnabled }),
  /** The coin icon players see (client listing reads sitePaymentAssets.imageUrl). */
  uploadAssetImage: (id: number, image: File) => {
    const formData = new FormData();
    formData.append('image', image);
    return axiosInstance.post(`/admin/payments/assets/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAssetImage: (id: number) => axiosInstance.delete(`/admin/payments/assets/${id}/image`),
  toggleNetwork: (id: number, isEnabled: boolean) =>
    axiosInstance.patch(`/admin/payments/networks/${id}/toggle`, { isEnabled }),
  /** Point one direction of a network at a provider row (a number assigns; omit = unchanged). */
  setNetworkRail: (id: number, body: { depositMethodId?: number; withdrawMethodId?: number }) =>
    axiosInstance.patch(`/admin/payments/networks/${id}`, body),
  updateAsset: (id: number, body: { name?: string; sortOrder?: number }) =>
    axiosInstance.patch(`/admin/payments/assets/${id}`, body),
  updateNetwork: (id: number, body: { name?: string; sortOrder?: number }) =>
    axiosInstance.patch(`/admin/payments/networks/${id}`, body),
  /** Global coin order — what players see. */
  reorderAssets: (items: { id: number; sortOrder: number }[]) =>
    axiosInstance.patch('/admin/payments/assets/reorder', { items }),
  /** Network order under one coin (all items must belong to the same coin). */
  reorderNetworks: (items: { id: number; sortOrder: number }[]) =>
    axiosInstance.patch('/admin/payments/networks/reorder', { items }),
};
